import { AlertBanner } from '../design-system/components/AlertBanner';
import { useAuth } from '../auth';
import { audienceFor } from '../auth/roles';
import { useSites } from '../api/hooks/useSites';
import { ApiClientError } from '../api/v1-client';
import { getRuntimeConfig } from '../config/runtime-config';

/**
 * What to say when there is no selected site.
 *
 * WHY THIS EXISTS
 * ---------------
 * Seven screens carried a byte-identical banner:
 *
 *     "Your account has no site assignments yet. Contact an
 *      administrator to get access."
 *
 * For a site-operations role with no assignment that is true and
 * actionable. For everyone else it is neither, and we spent a real
 * incident on it: a user was reported as "signed in, sees no sites",
 * the administrator went looking for them in the admin console to grant
 * a site, and could not find them — because the message had sent them
 * to solve a problem that did not exist.
 *
 * The three ways it is wrong:
 *
 *  - A `platform_admin` bypasses `user_site_assignments` entirely and
 *    already sees every site. An empty list means no site is
 *    COMMISSIONED, which no grant will fix.
 *  - A support role belongs on the support console, which has its own
 *    all-sites view. No assignment on this console will ever help.
 *  - A token carrying NO recognised role is the common real cause, and
 *    the one the old message hid completely. It usually means the
 *    identity-provider role key is wrong (the platform matches BARE
 *    keys — a `nexsoss:`-prefixed key renders here as a full role and
 *    matches nothing server-side) or the user has no project grant, so
 *    the roles claim is absent.
 *
 * This component says which of the four it is. It cannot be certain —
 * it reads the token the browser holds, not the backend's view of it —
 * so the wording points at what to check rather than asserting a
 * diagnosis.
 */

/** Roles that require a per-site assignment to see anything. */
const SITE_ROLES = [
  'nexsoss:site_supervisor',
  'nexsoss:foh_operator',
  'nexsoss:service_operator',
] as const;

const SUPPORT_ROLES = ['nexsoss:support_l2', 'nexsoss:support_l3'] as const;

/**
 * The sibling consoles' URLs.
 *
 * Derived from `apiBaseUrl`'s host by swapping the leading `api.`
 * label, because every console in a deployment is a sibling hostname of
 * the API front door (`operator.`/`support.`/`admin.` + the same
 * suffix) — see the deploy repo's ingress. Deliberately NOT a new
 * runtime-config field: those files are placeholder-substituted by
 * ansible, whose final guard greps for a fixed list of four known
 * tokens, so a newly invented placeholder would ship unsubstituted
 * without failing the run.
 *
 * Returns null rather than guessing when the shape does not match, and
 * the caller then omits the link instead of rendering a dead one.
 */
function siblingConsoleUrl(which: 'support' | 'admin'): string | null {
  try {
    const url = new URL(getRuntimeConfig().apiBaseUrl);
    if (!url.hostname.startsWith('api.')) return null;
    url.hostname = `${which}.${url.hostname.slice('api.'.length)}`;
    url.pathname = '/';
    return url.toString();
  } catch {
    return null;
  }
}

export function NoSiteNotice() {
  const { roles } = useAuth();
  const held = roles ?? [];
  // Same query key as SiteProvider's, so react-query serves this from
  // cache -- no second request.
  const sitesQuery = useSites();

  // A FAILED request must not render as "no sites". SiteProvider does
  // `sitesQuery.data ?? []`, so an error and a successful empty list
  // produce the identical empty array, and every message below would
  // then confidently explain an access situation that was never
  // established.
  //
  // THIS IS THE COMMON CASE ON A NEW MACHINE, and it is the one that
  // cost us an incident. The consoles are served from
  // `operator.<host>` while `apiBaseUrl` is `api.<host>` -- a
  // DIFFERENT ORIGIN, behind the same self-signed CA. A browser offers
  // its click-through interstitial for the page you navigate to, and
  // NOT for a fetch to another origin: that fails at the TLS layer,
  // silently, with no way for the user to accept it. So the console
  // loads perfectly and every API call fails, which presents as "I am
  // signed in and there are no sites" -- for any role, including
  // platform_admin, who bypasses site assignments entirely.
  //
  // `ApiClientError` means the server answered (401/403/500). Anything
  // else means the response never arrived, and on a host whose console
  // just loaded, an untrusted CA is far and away the likeliest reason.
  if (sitesQuery.isError) {
    const answered = sitesQuery.error instanceof ApiClientError;
    const site = audienceFor(held) === 'site';
    return (
      // The hotel's title and its answered branch have no "API" in them
      // and no status code (review BC-F10, 2026-10-31); ops keep both.
      <AlertBanner variant="error" title={site ? "Can't reach the system" : 'Cannot reach the platform API'}>
        {answered && site ? (
          <>
            The system turned down the request for the site list, so it could not be loaded at
            all. This is not about which sites you can see. Reload; if it keeps happening, call
            Nexsoss.
          </>
        ) : answered ? (
          <>
            The API rejected the request ({(sitesQuery.error as ApiClientError).status}). This
            is not a site-assignment problem — the site list could not be loaded at all.
          </>
        ) : site ? (
          // Hotel staff cannot run a script from the deploy repo, so they
          // get the cause in plain words and who to call (2026-10-31).
          <>
            The request never got a response, so this is not a statement about your access.
            On a device that has not been set up for this console the usual cause is that it
            does not trust the console&apos;s security certificate yet. Call Nexsoss to set this
            device up, then reload.
          </>
        ) : (
          <>
            The request never got a response, so this is not a statement about your access.
            On a machine that has not been set up for this deployment the usual cause is{' '}
            <strong>the platform&apos;s certificate not being trusted</strong>: the console and
            the API are separate hostnames, and a browser will let you click through a
            warning for the page but never for a background API call — so the console loads
            and every request fails. Run <code>scripts/install-ca.sh</code> from the deploy
            repo on this machine, then reload.
          </>
        )}
      </AlertBanner>
    );
  }

  const isAdmin = held.includes('nexsoss:platform_admin');
  const isSupport = SUPPORT_ROLES.some((r) => held.includes(r));
  const isSiteRole = SITE_ROLES.some((r) => held.includes(r));

  if (isAdmin) {
    // platform_admin bypasses assignments, so an empty list is a
    // statement about the deployment, not about this account.
    return (
      <AlertBanner variant="warning" title="No sites are commissioned">
        Your account can see every site, so this list being empty means no site has been
        commissioned yet — not that you are missing access. Commission one in the admin
        console.
      </AlertBanner>
    );
  }

  if (isSupport) {
    const url = siblingConsoleUrl('support');
    return (
      <AlertBanner variant="warning" title="This is the operator console">
        Your account holds a platform support role, which reads sites through the support
        console rather than this one. A site assignment here would not change that.{' '}
        {url ? <a href={url}>Open the support console</a> : 'Open the support console.'}
      </AlertBanner>
    );
  }

  if (isSiteRole) {
    // The original message, kept verbatim -- for this case it was
    // always correct.
    return (
      <AlertBanner variant="warning" title="No site assigned">
        Your account has no site assignments yet. Contact an administrator to get access.
      </AlertBanner>
    );
  }

  // No recognised role. The likeliest real cause, and the one the old
  // message hid.
  return (
    <AlertBanner variant="warning" title="Your account has no role here">
      You are signed in, but this deployment does not recognise any role on your account, so
      there is nothing to show. This is usually an identity-provider setting rather than
      anything about you: ask an administrator to check that your role is granted on the
      project (not just the organisation) and that its key has no prefix. Signing out and
      back in is required after either is changed.
    </AlertBanner>
  );
}
